// =============================================================================
// FOCUS ACTIONS SERVICE - Bepaal focuswaardige acties
// =============================================================================
//
// Deze service bepaalt welke bestaande acties "focuswaardig" zijn op basis van:
// - Actieve mijlpaal-categorie
// - Actie-status (niet voltooid)
// - Leeftijd van actie (oudere acties = meer vermijding)
// - Vermijdingssignalen
//
// BELANGRIJK:
// - Geen nieuwe acties aanmaken
// - Geen automatische planning
// - Maximaal 1 focus-actie tegelijk (topFocusAction)
// - Alleen suggestie, geen verplichte actie
//
// =============================================================================

import {
  Goal,
  Milestone,
  Action,
  GoalFocus,
  CategorizedMilestone,
} from '../types';

// -----------------------------------------------------------------------------
// TYPES
// -----------------------------------------------------------------------------

export interface FocusWorthyAction {
  actionId: string;         // ID van de actie
  priority: number;         // 1 (hoogste) tot N (laagste) - STABIEL, gebruik dit voor ranking
  score: number;            // ⚠️ RELATIEF binnen deze run - alleen voor debugging!
  reasons: string[];        // Interne redenen (niet voor UI labels!)
}

// -----------------------------------------------------------------------------
// SCORING CRITERIA
// -----------------------------------------------------------------------------

/**
 * Bereken focus-score voor een actie
 * Hogere score = meer focuswaardig (binnen deze detectie-run)
 * 
 * ⚠️ BELANGRIJK: Scores zijn relatief binnen één detectie-run.
 * 
 * SCORE-EIGENSCHAPPEN:
 * - Hogere score = meer focuswaardig (binnen deze run)
 * - Scores zijn NIET vergelijkbaar tussen runs (andere context/acties)
 * - Scores hebben GEEN absolute betekenis (geen thresholds!)
 * 
 * CORRECT GEBRUIK:
 * ✅ Sorteer acties binnen één run op score
 * ✅ Gebruik priority (1, 2, 3...) voor stabiele ranking
 * ✅ Score alleen voor debugging/logging
 * 
 * INCORRECT GEBRUIK:
 * ❌ if (score > 10) { critical() }  // Absolute threshold
 * ❌ Compare scores tussen verschillende runs
 * ❌ Opslaan en later vergelijken met nieuwe scores
 * 
 * @returns { score, reasons } - Score is relatief, priority is absoluut (1-N)
 */
function calculateActionScore(
  action: Action,
  milestone: Milestone | undefined,
  activeFocus: GoalFocus | null
): { score: number; reasons: string[] } {
  let score = 0;
  const reasons: string[] = [];

  // === CRITERIUM 1: Past bij actieve focus-categorie ===
  // Acties binnen de focus-categorie krijgen hoge prioriteit
  if (activeFocus && milestone) {
    const categorizedMilestone = milestone as CategorizedMilestone;
    if (categorizedMilestone.category === activeFocus.category) {
      score += 10;
      reasons.push('matches_focus_category');
    }
  }

  // === CRITERIUM 2: Leeftijd (oudere acties = meer vermijding) ===
  // Acties die lang geleden zijn aangemaakt maar nog niet voltooid
  const daysSinceCreation = (Date.now() - action.createdAt) / (1000 * 60 * 60 * 24);
  
  if (daysSinceCreation > 30) {
    // 30+ dagen oud = hoge vermijding
    score += 8;
    reasons.push('aged_30plus_days');
  } else if (daysSinceCreation > 14) {
    // 14-30 dagen oud = matige vermijding
    score += 5;
    reasons.push('aged_14plus_days');
  } else if (daysSinceCreation > 7) {
    // 7-14 dagen oud = lichte vermijding
    score += 3;
    reasons.push('aged_7plus_days');
  }

  // === CRITERIUM 3: Orphan actions (geen mijlpaal = structuurprobleem) ===
  // Acties zonder mijlpaal wijzen op gebrek aan structuur
  if (!action.milestoneId) {
    score += 6;
    reasons.push('orphan_action');
  }

  // === CRITERIUM 4: Mijlpaal zonder datum (onhelderheid) ===
  // Acties binnen mijlpalen zonder targetDate = onduidelijke planning
  if (milestone && !milestone.targetDate) {
    score += 4;
    reasons.push('milestone_no_date');
  }

  // === CRITERIUM 5: Recent aangemaakt maar al vermijdingssignalen ===
  // Nieuwe acties die toch al signalen tonen
  if (daysSinceCreation <= 3 && score > 0) {
    score += 2;
    reasons.push('recent_but_signals');
  }

  return { score, reasons };
}

// -----------------------------------------------------------------------------
// PUBLIC API
// -----------------------------------------------------------------------------

/**
 * Bepaal welke acties focuswaardig zijn
 * 
 * @param goal - Het doel
 * @param milestones - Alle mijlpalen (worden gefilterd)
 * @param actions - Alle acties (worden gefilterd)
 * @param activeFocus - Actieve focus voor dit doel (kan null zijn)
 * @returns Gesorteerde lijst van focuswaardige acties (hoogste priority eerst)
 */
export function getFocusWorthyActions(
  goal: Goal,
  milestones: Milestone[],
  actions: Action[],
  activeFocus: GoalFocus | null
): FocusWorthyAction[] {
  // Filter: alleen incomplete acties voor dit doel
  const incompleteActions = actions.filter(a => 
    a.goalId === goal.id && !a.completed
  );

  // Als er geen incomplete acties zijn, return lege array
  if (incompleteActions.length === 0) {
    return [];
  }

  // Score elke actie
  const scored = incompleteActions.map(action => {
    // Vind de bijbehorende mijlpaal (als die er is)
    const milestone = action.milestoneId 
      ? milestones.find(m => m.id === action.milestoneId)
      : undefined;

    // Bereken score en redenen
    const { score, reasons } = calculateActionScore(action, milestone, activeFocus);

    return {
      actionId: action.id,
      score,
      reasons,
    };
  });

  // Filter: alleen acties met score > 0
  const worthy = scored.filter(s => s.score > 0);

  // Als er geen waardige acties zijn, return lege array
  if (worthy.length === 0) {
    return [];
  }

  // Sorteer op score (hoogste eerst)
  const sorted = worthy.sort((a, b) => b.score - a.score);

  // Return met priority rank (1 = hoogste)
  // BELANGRIJK: Gebruik priority, niet score, voor stabiele ranking
  // - priority is absoluut (1 is altijd hoogste, ongeacht scores)
  // - score is relatief (alleen vergelijkbaar binnen deze run)
  return sorted.map((item, index) => ({
    actionId: item.actionId,
    priority: index + 1,  // ✅ STABIEL: gebruik dit voor ranking/logica
    score: item.score,    // ⚠️ RELATIEF: alleen voor debugging
    reasons: item.reasons,
  }));
}

/**
 * Krijg de TOP 1 focus-actie
 * Dit is de actie die de hoogste prioriteit heeft
 * 
 * @param goal - Het doel
 * @param milestones - Alle mijlpalen
 * @param actions - Alle acties
 * @param activeFocus - Actieve focus (kan null zijn)
 * @returns ID van de top focus-actie, of null als er geen waardige actie is
 */
export function getTopFocusAction(
  goal: Goal,
  milestones: Milestone[],
  actions: Action[],
  activeFocus: GoalFocus | null
): string | null {
  const worthy = getFocusWorthyActions(goal, milestones, actions, activeFocus);
  return worthy.length > 0 ? worthy[0].actionId : null;
}

/**
 * Check of een actie focuswaardig is
 * 
 * @param actionId - ID van de actie om te checken
 * @param goal - Het doel
 * @param milestones - Alle mijlpalen
 * @param actions - Alle acties
 * @param activeFocus - Actieve focus (kan null zijn)
 * @returns true als de actie in de focuswaardige lijst zit
 */
export function isActionFocusWorthy(
  actionId: string,
  goal: Goal,
  milestones: Milestone[],
  actions: Action[],
  activeFocus: GoalFocus | null
): boolean {
  const worthy = getFocusWorthyActions(goal, milestones, actions, activeFocus);
  return worthy.some(w => w.actionId === actionId);
}

/**
 * Krijg de focus-priority voor een specifieke actie
 * 
 * @param actionId - ID van de actie
 * @param goal - Het doel
 * @param milestones - Alle mijlpalen
 * @param actions - Alle acties
 * @param activeFocus - Actieve focus (kan null zijn)
 * @returns Priority nummer (1 = hoogste) of null als niet focuswaardig
 */
export function getActionFocusPriority(
  actionId: string,
  goal: Goal,
  milestones: Milestone[],
  actions: Action[],
  activeFocus: GoalFocus | null
): number | null {
  const worthy = getFocusWorthyActions(goal, milestones, actions, activeFocus);
  const found = worthy.find(w => w.actionId === actionId);
  return found ? found.priority : null;
}
